"use client";
import { Link } from "@/components/Link";
import { SearchField } from "@/components/Search";
import { TEST_IDS } from "@/constants/testIds";
import { useCart } from "@/contexts/cartContext";
import { useSession } from "@/contexts/sessionContext";
import { HeaderType, useHeaderType } from "@/hooks/useHeaderType";
import { useRouter } from "@/hooks/useRouter";
import {
  Button,
  SvgAccountFilled,
  SvgBarCode,
  SvgCart,
  SvgDashboard,
  SvgHamburger,
} from "@getgrover/ui";
import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useEffect, useState } from "react";
import { IconLink } from "../IconLink/IconLink";
import {
  CartItemCounter,
  HeaderContainer,
  NavItem,
  NavList,
  NavigationWrapper,
  SearchWrapper,
  StyledLink,
} from "./styles";

const Menu = dynamic(() => import("@/components/Menu").then((res) => res.Menu));

const items = [
  {
    name: "Account",
    icon: SvgAccountFilled,
    href: "/account",
    ariaLabelKey: "aria_label.Account_icon" as const,
  },
  {
    name: "Dashboard",
    icon: SvgDashboard,
    href: "/orders",
    ariaLabelKey: "aria_label.Dashboard_icon" as const,
  },
];

const renderLogo = (loggedIn: boolean) =>
  loggedIn ? (
    <Link href="/">
      <Image
        src="/images/logo.svg"
        alt="Grover Logo"
        width={96}
        height={32}
        loading="lazy"
      />
    </Link>
  ) : (
    <Image
      src="/images/logo.svg"
      alt="Grover Logo"
      width={96}
      height={32}
      loading="lazy"
    />
  );

export const Header: React.FC = () => {
  const { loggedIn } = useSession();
  const router = useRouter();
  const { type, Element, hideSearch } = useHeaderType();
  const { totalQuantity } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const t = useTranslations("header");

  const handleSearchClick = () => {
    router.push("/search");
  };

  const handleMenuClick = () => {
    setIsMenuOpen(true);
  };

  useEffect(() => {
    router.prefetch("/search");
  }, [router]);

  switch (type) {
    case HeaderType.None:
      return null;
    case HeaderType.Custom:
      return <Element />;
    case HeaderType.HiddenSearch:
      return (
        <HeaderContainer border={!loggedIn}>
          <NavigationWrapper>
            {renderLogo(loggedIn)}
            {loggedIn &&
              items.map((item) => (
                <IconLink
                  aria-label={t(item.ariaLabelKey)}
                  key={item.href}
                  SvgIcon={item.icon}
                  href={item.href}
                />
              ))}
          </NavigationWrapper>
        </HeaderContainer>
      );
    case HeaderType.Default:
      return (
        <HeaderContainer border={!loggedIn}>
          <NavigationWrapper>
            {renderLogo(loggedIn)}
            {loggedIn && (
              <nav>
                <NavList>
                  {items.map((item) => (
                    <NavItem key={item.href}>
                      <IconLink
                        aria-label={t(item.ariaLabelKey)}
                        SvgIcon={item.icon}
                        href={item.href}
                      />
                    </NavItem>
                  ))}
                  <NavItem>
                    <IconLink
                      testId={TEST_IDS.HEADER_CART_BUTTON}
                      aria-label={t("aria_label.Cart_icon")}
                      SvgIcon={SvgCart}
                      href="/cart"
                    />
                    {!!totalQuantity && (
                      <CartItemCounter>{totalQuantity}</CartItemCounter>
                    )}
                  </NavItem>
                </NavList>
              </nav>
            )}
          </NavigationWrapper>
          {!hideSearch && loggedIn && (
            <SearchWrapper>
              <Button
                aria-label="Menu"
                variant="Secondary"
                iconOnly
                size="M"
                onClick={handleMenuClick}
              >
                <SvgHamburger />
              </Button>
              <Menu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
              <SearchField onClick={handleSearchClick} />
              <StyledLink href="/barcode" aria-label={t("aria_label.barcode")}>
                <SvgBarCode />
              </StyledLink>
            </SearchWrapper>
          )}
        </HeaderContainer>
      );
  }
};
